import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { authFetch } from '@/lib/auth';
import { useNavigate, useLocation } from 'react-router-dom';

export type ActivityType = 'quiz' | 'flashcards' | 'summary' | 'lesson' | 'plan';
export type ActivityStatus = 'generating' | 'completed' | 'error';

export interface BackgroundActivity {
    id: string; // The session ID or study set ID
    type: ActivityType;
    title: string;
    status: ActivityStatus;
    progress?: number;
    error?: string;
    startTime: number;
    // Whether the terminal (success/error) toast has already been shown.
    // Lets a toast suppressed while the learner was mid-quiz fire once they
    // land somewhere it'd actually be useful, instead of never firing at all.
    notified?: boolean;
    /** Real progress, when the server reports it (quizzes: questions written so far). */
    ready?: number;
    total?: number;
    /**
     * No "still working" toasts, only the ready/failed one. For generations
     * the learner already watched start (e.g. the quiz creator's own "first
     * questions are ready" screen): a loading toast on top of that screen
     * contradicted it.
     */
    quiet?: boolean;
}

const ACTIVITY_LABEL: Record<ActivityType, string> = {
    quiz: "quiz",
    flashcards: "flashcards",
    summary: "summary",
    lesson: "lesson",
    plan: "study plan",
};

/** Where this activity's own "here's your result" page lives. */
const activityDestinationPath = (activity: Pick<BackgroundActivity, "type" | "id">) =>
    activity.type === "quiz"
        ? `/quiz/session/${activity.id}`
        : activity.type === "lesson"
            ? `/lesson/${activity.id}`
        : activity.type === "plan"
            ? `/planner`
            : `/study/${activity.id}/${activity.type}`;

/**
 * A toast about this background generation is noise, not help, when either:
 * - the learner is already sitting on that exact activity's destination page
 *   (its own in-page loading state already covers this, a toast on top of it
 *   is redundant), or
 * - the learner is mid-question in ANY quiz session — a toast popping up
 *   over the answer choices was the original complaint that drove this fix,
 *   and it's just as disruptive for a quiz other than the one it's about.
 */
const isToastSuppressed = (activity: Pick<BackgroundActivity, "type" | "id">, pathname: string) => {
    if (pathname === activityDestinationPath(activity)) return true;
    if (pathname.startsWith("/quiz/session/")) return true;
    return false;
};

interface AIActivityContextType {
    activities: BackgroundActivity[];
    trackActivity: (type: ActivityType, id: string, title: string, options?: { quiet?: boolean }) => void;
    removeActivity: (id: string) => void;
}

const AIActivityContext = createContext<AIActivityContextType | undefined>(undefined);

export const useAIActivity = () => {
    const context = useContext(AIActivityContext);
    if (!context) {
        throw new Error('useAIActivity must be used within AIActivityProvider');
    }
    return context;
};

export const getActivityStatus = (data: any): ActivityStatus | 'failed' | undefined => {
    if (!data) return undefined;
    const payload = data.data ?? data;
    return (
        payload?.status || 
        data?.status || 
        data?.data?.status || 
        payload?.study_set?.status || 
        data?.study_set?.status ||
        // Quizzes poll the session endpoint, which reports generation state
        // on session.metadata. Without this a quiz was never seen to finish:
        // its "still working" toast kept refreshing until the 10-minute
        // timeout and the "ready" toast never fired.
        data?.session?.metadata?.status
    );
};

/** Questions written so far vs. requested, for quiz sessions. */
export const quizCounts = (data: any): { ready: number; total: number } | null => {
    const total = Number(data?.session?.total_questions);
    const ready = typeof data?.ready_questions === 'number' ? data.ready_questions : data?.questions?.length;
    if (!Number.isInteger(ready) || ready < 0 || !Number.isFinite(total) || total <= 0) return null;
    return { ready, total };
};

const loadingDescription = (counts: { ready?: number; total?: number }) =>
    counts.total && counts.ready
        ? `${counts.ready} of ${counts.total} questions ready. This keeps going in the background.`
        : "This keeps going in the background. We'll tell you when it's ready.";

export const AIActivityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const queryClient = useQueryClient();
    const [activities, setActivities] = useState<BackgroundActivity[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    // The 3s poll interval below is set up once per generating-activity-count
    // change (see its dependency array) and reads location on every tick via
    // this ref, so a route change mid-generation doesn't need to tear down
    // and restart the interval to be seen.
    const locationRef = useRef(location.pathname);
    useEffect(() => {
        locationRef.current = location.pathname;
    }, [location.pathname]);

    // 1. Declare atomic state update helpers first
    const removeActivity = useCallback((id: string) => {
        setActivities(prev => prev.filter(a => a.id !== id));
        toast.dismiss(`ai-${id}`);
    }, []);

    const updateActivityStatus = useCallback((id: string, status: ActivityStatus, error?: string) => {
        setActivities(prev => prev.map(a => a.id === id ? { ...a, status, error } : a));
    }, []);

    // 2. Declare derived state
    const isAnyGenerating = useMemo(() => activities.some(a => a.status === 'generating'), [activities]);

    // 3. Declare UI/Toast helpers
    const showSuccessToast = useCallback((activity: BackgroundActivity) => {
        const path = activityDestinationPath(activity);
        const noun = ACTIVITY_LABEL[activity.type];
        const label = noun.charAt(0).toUpperCase() + noun.slice(1);
        
        try {
            if (typeof (window as any).PushAlerts === 'object') {
                (window as any).PushAlerts.send(`Your ${noun} is ready`, `"${activity.title}" is ready.`, path);
            }
        } catch (e) {
            console.error("PushAlerts failed to send notice", e);
        }

        toast.success(`${label} ready`, {
            id: `ai-${activity.id}`,
            description: `"${activity.title}" is ready.`,
            action: {
                label: activity.type === "quiz" ? "Open quiz" : "Open",
                onClick: () => {
                    navigate(path);
                    removeActivity(activity.id);
                }
            },
            duration: 10000,
        });
    }, [navigate, removeActivity]);

    const showErrorToast = useCallback((activity: BackgroundActivity) => {
        toast.error(`Couldn't finish your ${ACTIVITY_LABEL[activity.type]}`, {
            id: `ai-${activity.id}`,
            description: activity.type === "quiz" && activity.ready
                ? `"${activity.title}" stopped after ${activity.ready} of ${activity.total} questions. Those are kept: open it to continue, or make it again.`
                : `Something went wrong making "${activity.title}". Nothing else was affected. Try again.`,
            action: {
                label: "Dismiss",
                onClick: () => removeActivity(activity.id)
            }
        });
    }, [removeActivity]);

    const trackActivity = useCallback((type: ActivityType, id: string, title: string, options?: { quiet?: boolean }) => {
        const quiet = !!options?.quiet;
        setActivities(prev => {
            const exists = prev.some(a => a.id === id);
            if (exists) return prev;

            if (!quiet && !isToastSuppressed({ type, id }, locationRef.current)) {
                toast.loading(`Making your ${ACTIVITY_LABEL[type]}: ${title}`, {
                    id: `ai-${id}`,
                    description: loadingDescription({}),
                    // "Hide" only stops these notifications; the server keeps
                    // generating.
                    action: {
                        label: "Hide",
                        onClick: () => removeActivity(id)
                    }
                });
            }

            return [...prev, { id, type, title, status: 'generating', progress: 0, startTime: Date.now(), notified: false, quiet }];
        });
    }, [removeActivity]);

    // 4. Manage refs for polling
    const activitiesRef = useRef(activities);
    useEffect(() => {
        activitiesRef.current = activities;
    }, [activities]);

    // 5. Main Polling Effect
    useEffect(() => {
        if (!isAnyGenerating) return;

        const controller = new AbortController();
        const signal = controller.signal;
        let polling = false;
        const failures = new Map<string, { count: number; retryAt: number }>();

        const interval = setInterval(async () => {
            if (polling || document.hidden) return;
            const activeActivities = activitiesRef.current.filter(a => a.status === 'generating');
            if (activeActivities.length === 0) return;

            polling = true;
            try {
              await Promise.allSettled(activeActivities.map(async (activity) => {
                if (signal.aborted) return;
                if ((failures.get(activity.id)?.count ?? 0) >= 5) return;
                if (Date.now() - activity.startTime > 600000) {
                    updateActivityStatus(activity.id, 'error', 'Operation timed out');
                    return;
                }
                if (Date.now() < (failures.get(activity.id)?.retryAt ?? 0)) return;

                try {
                    let endpoint = '';
                    if (activity.type === 'quiz') endpoint = `/api/quizzes/v2/sets/${activity.id}?statusOnly=true`;
                    else if (activity.type === 'flashcards' || activity.type === 'lesson') endpoint = `/api/flashcards/sets/${activity.id}`;
                    else if (activity.type === 'summary') endpoint = `/api/summaries/sets/${activity.id}`;
                    else if (activity.type === 'plan') endpoint = `/api/planner/plans/${activity.id}`;

                    const res = await authFetch(endpoint, { signal: AbortSignal.any([signal, AbortSignal.timeout(30_000)]) });
                    if (signal.aborted) return;
                    
                    if (!res.ok) {
                        if ([401, 403, 404].includes(res.status)) {
                            failures.set(activity.id, { count: 5, retryAt: Infinity });
                            updateActivityStatus(activity.id, 'error', 'Activity is unavailable');
                            return;
                        }
                        throw new Error(`Activity request failed (${res.status})`);
                    }

                    const data = await res.json();
                    failures.delete(activity.id);
                    const payload = data.data ?? data;
                    const currentStatus = getActivityStatus(data);
                    const progress = data.progress || 0;
                    const counts = activity.type === 'quiz' ? quizCounts(data) : null;

                    if (signal.aborted) return;

                    if (
                        currentStatus === 'completed' ||
                        (activity.type === 'summary' && !!(payload.summary?.content)) ||
                        (counts && counts.ready >= counts.total)
                    ) {
                        // The terminal toast itself is fired by the dedicated
                        // effect below (keyed on route + `notified`), so a
                        // resolution that lands while the learner is mid-quiz
                        // or already on the destination page doesn't get lost
                        // — it fires the moment either condition clears
                        // instead of being suppressed forever.
                        if (activity.type === 'plan') {
                            const schedule = payload.plan?.daily_schedule;
                            if (!Array.isArray(schedule) || schedule.length === 0) { updateActivityStatus(activity.id, 'error', 'The plan did not contain saved study sessions. Please try generating it again.'); return; }
                            void queryClient.invalidateQueries({queryKey:['planner']});
                            window.dispatchEvent(new CustomEvent('novari:plan-ready',{detail:{firstDate:schedule[0]?.date}}));
                        }
                        updateActivityStatus(activity.id, 'completed');
                    } else if (currentStatus === 'error' || currentStatus === 'failed') {
                        updateActivityStatus(activity.id, 'error', 'AI generation failed');
                    } else {
                        if (Date.now() - activity.startTime > 600000) {
                            updateActivityStatus(activity.id, 'error', 'Operation timed out');
                            removeActivity(activity.id);
                            return;
                        }

                        if (progress > 0 || counts) {
                            setActivities(prev => prev.map(a => a.id === activity.id ? { ...a, progress, ...(counts ?? {}) } : a));
                        }

                        if (activity.quiet || isToastSuppressed(activity, locationRef.current)) {
                            toast.dismiss(`ai-${activity.id}`);
                            return;
                        }

                        toast.loading(`Making your ${ACTIVITY_LABEL[activity.type]}: ${activity.title}`, {
                            id: `ai-${activity.id}`,
                            description: counts
                                ? loadingDescription(counts)
                                : progress > 0
                                    ? `${Math.round(progress)}% done. This keeps going in the background.`
                                    : loadingDescription({}),
                            action: {
                                label: "Hide",
                                onClick: () => removeActivity(activity.id)
                            }
                        });
                    }
                } catch (err) {
                    if (signal.aborted) return;
                    const count = (failures.get(activity.id)?.count ?? 0) + 1;
                    failures.set(activity.id, { count, retryAt: Date.now() + Math.min(30_000, 3000 * 2 ** (count - 1)) + Math.random() * 1000 });
                    if (count >= 5) updateActivityStatus(activity.id, 'error', 'Activity status could not be retrieved');
                    console.error(`Polling failed for ${activity.id}:`, err);
                }
              }));
            } finally {
                polling = false;
            }
        }, 3000);

        return () => {
            controller.abort();
            clearInterval(interval);
        };
    }, [isAnyGenerating, updateActivityStatus, removeActivity]);

    // 6. Fire the terminal (success/error) toast once it's actually useful.
    // A resolution that lands while the learner is on the activity's own
    // destination page or mid-quiz is deliberately NOT toasted above; this
    // effect re-checks every route change and every activities update, so
    // the toast fires the moment the learner lands somewhere it isn't
    // redundant or disruptive — instead of the notification being lost.
    useEffect(() => {
        const justNotified: string[] = [];
        activities.forEach(activity => {
            if (activity.status === 'generating' || activity.notified) return;
            if (isToastSuppressed(activity, location.pathname)) return;
            if (activity.status === 'completed') showSuccessToast(activity);
            else if (activity.status === 'error') showErrorToast(activity);
            justNotified.push(activity.id);
        });
        if (justNotified.length > 0) {
            setActivities(prev => prev.map(a => justNotified.includes(a.id) ? { ...a, notified: true } : a));
        }
    }, [location.pathname, activities, showSuccessToast, showErrorToast]);

    return (
        <AIActivityContext.Provider value={{ activities, trackActivity, removeActivity }}>
            {children}
        </AIActivityContext.Provider>
    );
};
